import { useCallback } from 'react';

import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  BillingCurrency,
  BillingInterval,
  TeamBillingPriceCurrencyOption,
} from '@this+that/models';

export interface TeamBillingPaymentMethod {
  brand?: string;
  last4?: string;
  expMonth?: number;
  expYear?: number;
}

export interface TeamBillingUpcomingInvoice {
  amountDue: number;
  currency: string;
}

export interface TeamBillingInvoice {
  created: number;
  total: number;
  currency: string;
  status: string;
  hostedInvoiceUrl?: string;
  invoicePdf?: string;
}

export interface TeamBillingPrice {
  interval: BillingInterval;
  unitAmount: number;
  currency: string;
  lookupKey: string;
  currencyOptions: TeamBillingPriceCurrencyOption[];
}

export interface TeamBilling {
  subscriptionStatus?: string;
  stripeTrialEnd?: number;
  planInterval?: string;
  priceLookupKey?: string;
  priceUnitAmount?: number;
  priceCurrency?: string;
  seatQuantity?: number;
  currentPeriodStart?: number;
  currentPeriodEnd?: number;
  cancelAtPeriodEnd?: boolean;
  lastInvoiceStatus?: string;
  pastDueReason?: string;
  nextPaymentAttempt?: number;
  billingEmail?: string;
  paymentMethod?: TeamBillingPaymentMethod;
  upcomingInvoice?: TeamBillingUpcomingInvoice;
  invoices: TeamBillingInvoice[];
  prices: TeamBillingPrice[];
}

interface GetTeamBillingData {
  getTeamBilling: TeamBilling;
}

const GET_TEAM_BILLING = gql`
  query getTeamBilling($teamId: String!) {
    getTeamBilling(teamId: $teamId) {
      subscriptionStatus
      stripeTrialEnd
      planInterval
      priceLookupKey
      priceUnitAmount
      priceCurrency
      seatQuantity
      currentPeriodStart
      currentPeriodEnd
      cancelAtPeriodEnd
      lastInvoiceStatus
      pastDueReason
      nextPaymentAttempt
      billingEmail
      paymentMethod {
        brand
        last4
        expMonth
        expYear
      }
      upcomingInvoice {
        amountDue
        currency
      }
      invoices {
        created
        total
        currency
        status
        hostedInvoiceUrl
        invoicePdf
      }
      prices {
        interval
        unitAmount
        currency
        lookupKey
        currencyOptions {
          currency
          unitAmount
          taxBehavior
        }
      }
    }
  }
`;

export function useGetTeamBilling() {
  const { loading, refetch } = useQuery<GetTeamBillingData>(GET_TEAM_BILLING, {
    skip: true,
  });

  const getTeamBilling = useCallback(
    async (teamId: string) => {
      const { data } = await refetch({ teamId });
      return data?.getTeamBilling;
    },
    [refetch],
  );

  return { getTeamBilling, loading };
}

interface CreateBillingCheckoutSessionData {
  createBillingCheckoutSession: string;
}

const CREATE_BILLING_CHECKOUT_SESSION = gql`
  mutation createBillingCheckoutSession(
    $teamId: String!
    $interval: String!
    $currency: String
  ) {
    createBillingCheckoutSession(
      teamId: $teamId
      interval: $interval
      currency: $currency
    )
  }
`;

export function useCreateBillingCheckoutSession() {
  const [createBillingCheckoutSessionMutation, { loading, error }] =
    useMutation<CreateBillingCheckoutSessionData>(
      CREATE_BILLING_CHECKOUT_SESSION,
    );

  const createBillingCheckoutSession = useCallback(
    async (
      teamId: string,
      interval: BillingInterval,
      currency?: BillingCurrency,
    ) => {
      const { data } = await createBillingCheckoutSessionMutation({
        variables: { teamId, interval, currency },
      });
      return data?.createBillingCheckoutSession;
    },
    [createBillingCheckoutSessionMutation],
  );

  return { createBillingCheckoutSession, loading, error };
}

interface CreateBillingPortalSessionData {
  createBillingPortalSession: string;
}

const CREATE_BILLING_PORTAL_SESSION = gql`
  mutation createBillingPortalSession($teamId: String!) {
    createBillingPortalSession(teamId: $teamId)
  }
`;

export function useCreateBillingPortalSession() {
  const [createBillingPortalSessionMutation, { loading, error }] =
    useMutation<CreateBillingPortalSessionData>(CREATE_BILLING_PORTAL_SESSION);

  const createBillingPortalSession = useCallback(
    async (teamId: string) => {
      const { data } = await createBillingPortalSessionMutation({
        variables: { teamId },
      });
      return data?.createBillingPortalSession;
    },
    [createBillingPortalSessionMutation],
  );

  return { createBillingPortalSession, loading, error };
}

interface CancelTeamSubscriptionData {
  cancelTeamSubscription: boolean;
}

const CANCEL_TEAM_SUBSCRIPTION = gql`
  mutation cancelTeamSubscription($teamId: String!) {
    cancelTeamSubscription(teamId: $teamId)
  }
`;

export function useCancelTeamSubscription() {
  const [cancelTeamSubscriptionMutation, { loading, error }] =
    useMutation<CancelTeamSubscriptionData>(CANCEL_TEAM_SUBSCRIPTION);

  const cancelTeamSubscription = useCallback(
    async (teamId: string) => {
      const { data } = await cancelTeamSubscriptionMutation({
        variables: { teamId },
      });
      return data?.cancelTeamSubscription;
    },
    [cancelTeamSubscriptionMutation],
  );

  return { cancelTeamSubscription, loading, error };
}
